import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { eventService } from '../services/eventService';
import { registrationService } from '../services/registrationService';
import { reminderService } from '../services/reminderService';
import { collaboratorService } from '../services/collaboratorService';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import './EventDetails.css';

const urlSys = process.env.REACT_APP_SHARE_URL || 'http://localhost:3000';

const escapeHtml = (text) =>
  text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const renderPreview = (text) => {
  const escaped = escapeHtml(text || '');
  return escaped
    .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    .replace(/~(.*?)~/g, '<del>$1</del>')
    .replace(/```(.*?)```/g, '<code>$1</code>')
    .replace(/\n/g, '<br />');
};

const showConfirmation = (message, onConfirm) => {
  const ConfirmToast = ({ closeToast }) => (
    <div>
      <p style={{ marginBottom: '1rem' }}>{message}</p>
      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
        <button
          onClick={() => {
            closeToast();
          }}
          style={{
            padding: '0.5rem 1rem',
            border: '1px solid #ccc',
            borderRadius: '4px',
            background: 'white',
            cursor: 'pointer'
          }}
        >
          Não
        </button>
        <button
          onClick={() => {
            onConfirm();
            closeToast();
          }}
          style={{
            padding: '0.5rem 1rem',
            border: 'none',
            borderRadius: '4px',
            background: '#007bff',
            color: 'white',
            cursor: 'pointer'
          }}
        >
          Sim
        </button>
      </div>
    </div>
  );

  toast(<ConfirmToast />, {
    position: 'top-center',
    autoClose: false,
    closeButton: false,
    draggable: false,
    closeOnClick: false
  });
};

const EventDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('registrations');
  const [importing, setImporting] = useState(false);
  const [winnerModalOpen, setWinnerModalOpen] = useState(false);
  const [winnerData, setWinnerData] = useState(null);
  const [addCollaboratorModalOpen, setAddCollaboratorModalOpen] = useState(false);
  const [newCollaborator, setNewCollaborator] = useState({ name: '', email: '', role: 'editor' });
  const [tempPassword, setTempPassword] = useState(null);
  const fileInputRef = useRef(null);

  // Verificar permissões do usuário
  const isOwner = !user?.isCollaborator;
  const isManager = user?.isCollaborator && user?.role === 'manager';
  const isEditor = user?.isCollaborator && user?.role === 'editor';
  const isViewer = user?.isCollaborator && user?.role === 'viewer';
  const canEdit = isOwner || isManager;
  const canManageRegistrations = isOwner || isManager || isEditor;
  const canManageReminders = isOwner || isManager || isEditor;

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [eventData, regData, remData, collabData] = await Promise.all([
        eventService.getById(id),
        registrationService.getByEventId(id),
        reminderService.getByEventId(id),
        collaboratorService.getByEventId(id).catch(() => ({ collaborators: [] }))
      ]);
      setEvent(eventData.event);
      setRegistrations(regData.registrations);
      setReminders(remData.reminders);
      setCollaborators(collabData.collaborators || []);
    } catch (error) {
      toast.error('Erro ao carregar dados');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      const blob = await registrationService.exportCSV(id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inscricoes_${event.title}.csv`;
      a.click();
      toast.success('Exportado com sucesso!');
    } catch (error) {
      toast.error('Erro ao exportar');
    }
  };

  const handleDrawWinner = async () => {
    try {
      const data = await registrationService.drawWinner(id);
      toast.success(`🎉 Vencedor: ${data.winner.name} - ${data.winner.phone}`);
      setWinnerData(data.winner);
      setWinnerModalOpen(true);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao sortear');
    }
  };

  const handleDeleteRegistration = async (regId) => {
    showConfirmation('Tem certeza que deseja excluir esta inscrição?', async () => {
      try {
        await registrationService.delete(id, regId);
        toast.success('Inscrição removida');
        loadData();
      } catch (error) {
        toast.error('Erro ao remover');
      }
    });
  };

  const handleToggleAcceptMessages = async (regId) => {
    try {
      await registrationService.toggleAcceptMessages(id, regId);
      toast.success('Status de mensagens atualizado');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar');
    }
  };

  const copyShareLink = () => {
    const link = `${urlSys}/evento/${event.share_token}`;
    navigator.clipboard.writeText(link);
    toast.success('Link copiado!');
  };

  const handleDeleteReminder = async (reminderId) => {
    showConfirmation('Tem certeza que deseja remover este lembrete?', async () => {
      try {
        await reminderService.delete(id, reminderId);
        toast.success('Lembrete removido');
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Erro ao remover lembrete');
      }
    });
  };

  const handleDuplicateReminder = (reminder) => {
    navigate(`/events/${id}/reminders/new`, {
      state: {
        duplicate: true,
        daysBefore: reminder.days_before,
        message: reminder.message,
        imageUrl: reminder.image_url
      }
    });
  };

  const handleImportXLSX = async (eventChange) => {
    const file = eventChange.target.files?.[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();
    if (!fileName.endsWith('.xlsx') && !fileName.endsWith('.xls')) {
      toast.error('Envie um arquivo XLSX');
      eventChange.target.value = '';
      return;
    }

    setImporting(true);
    try {
      const { summary } = await registrationService.importXLSX(id, file);
      const details = [
        `${summary.imported} adicionados`,
        `${summary.duplicates} duplicados`,
        `${summary.invalid} inválidos`,
        summary.limitReached ? 'limite de vagas alcançado' : null
      ]
        .filter(Boolean)
        .join(' • ');

      toast.success(`Importação concluída: ${details}`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao importar inscritos');
    } finally {
      setImporting(false);
      eventChange.target.value = '';
    }
  };

  const triggerFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAddCollaborator = async (e) => {
    e.preventDefault();
    try {
      const response = await collaboratorService.create(id, newCollaborator);
      setTempPassword(response.collaborator.tempPassword);
      toast.success('Colaborador adicionado com sucesso!');
      setNewCollaborator({ name: '', email: '', role: 'editor' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao adicionar colaborador');
    }
  };

  const handleRemoveCollaborator = async (collaboratorId) => {
    showConfirmation('Tem certeza que deseja remover este colaborador?', async () => {
      try {
        await collaboratorService.remove(collaboratorId);
        toast.success('Colaborador removido');
        loadData();
      } catch (error) {
        toast.error('Erro ao remover colaborador');
      }
    });
  };

  const handleUpdateRole = async (collaboratorId, newRole) => {
    try {
      await collaboratorService.updateRole(collaboratorId, newRole);
      toast.success('Permissão atualizada');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar permissão');
    }
  };

  const handleResetPassword = async (collaboratorId, collaboratorName) => {
    showConfirmation(
      `Resetar a senha de ${collaboratorName}? Uma nova senha temporária será gerada.`,
      async () => {
        try {
          const response = await collaboratorService.resetPassword(collaboratorId);
          setTempPassword(response.tempPassword);
          setAddCollaboratorModalOpen(true);
          toast.success('Senha resetada com sucesso!');
        } catch (error) {
          toast.error(error.response?.data?.error || 'Erro ao resetar senha');
        }
      }
    );
  };

  const closeCollaboratorModal = () => {
    setAddCollaboratorModalOpen(false);
    setNewCollaborator({ name: '', email: '', role: 'editor' });
    setTempPassword(null);
  };

  const closeWinnerModal = () => {
    setWinnerModalOpen(false);
    setWinnerData(null);
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!event) return null;

  return (
    <div className="event-details">
      <div className="container">
        <div className="event-details-header">
          <div>
            <h1>{event.title}</h1>
            <p>📅 {format(new Date(event.start_date), 'dd/MM/yyyy HH:mm')}</p>
            <p>📍 {event.location}</p>
          </div>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {canEdit && (
              <Link to={`/events/${id}/edit`} className="btn btn-outline">
                Editar
              </Link>
            )}
            <button onClick={copyShareLink} className="btn btn-primary">
              📋 Copiar Link
            </button>
          </div>
        </div>

        <div className="tabs">
          <button
            className={`tab ${activeTab === 'registrations' ? 'active' : ''}`}
            onClick={() => setActiveTab('registrations')}
          >
            Inscrições ({registrations.length})
          </button>
          <button
            className={`tab ${activeTab === 'reminders' ? 'active' : ''}`}
            onClick={() => setActiveTab('reminders')}
          >
            Lembretes ({reminders.length})
          </button>
          {isOwner && (
            <button
              className={`tab ${activeTab === 'collaborators' ? 'active' : ''}`}
              onClick={() => setActiveTab('collaborators')}
            >
              Colaboradores ({collaborators.length})
            </button>
          )}
        </div>

        {activeTab === 'registrations' && (
          <div className="tab-content">
            {canManageRegistrations && (
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <button onClick={handleExportCSV} className="btn btn-success">
                  📤 Exportar CSV
                </button>
                <button onClick={handleDrawWinner} className="btn btn-secondary">
                  🎲 Sortear Vencedor
                </button>
                <button 
                  onClick={triggerFilePicker} 
                  className="btn btn-outline" 
                  disabled={importing}
                  title="Envie um arquivo XLSX com os campos: nome, telefone e email (opcional)"
                >
                  {importing ? 'Importando...' : '📥 Importar XLSX'}
                </button>
                <a 
                  href="/exemplo.xlsx" 
                  download="modelo_importacao.xlsx"
                  className="btn btn-outline"
                  title="Baixar arquivo modelo com os campos: nome, telefone e email"
                >
                  📄 Modelo XLSX
                </a>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={handleImportXLSX}
                />
              </div>
            )}

{registrations.length === 0 ? (
              <p>Nenhuma inscrição ainda</p>
            ) : (() => {
              // Ordenar inscrições por nome alfabeticamente
              const sortedRegistrations = [...registrations].sort((a, b) => 
                a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
              );

              // Agrupar por letra inicial
              const groupedByLetter = sortedRegistrations.reduce((acc, reg) => {
                const firstLetter = reg.name.charAt(0).toUpperCase();
                if (!acc[firstLetter]) {
                  acc[firstLetter] = [];
                }
                acc[firstLetter].push(reg);
                return acc;
              }, {});

              const letters = Object.keys(groupedByLetter).sort();

              return (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Telefone</th>
                      <th>Email</th>
                      <th>WhatsApp</th>
                      <th>Data</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {letters.map((letter) => (
                      <React.Fragment key={letter}>
                        <tr style={{ backgroundColor: '#f0f0f0' }}>
                          <td colSpan="6" style={{ fontWeight: 'bold', fontSize: '1.1rem', padding: '0.75rem' }}>
                            {letter}
                          </td>
                        </tr>
                        {groupedByLetter[letter].map((reg) => (
                          <tr key={reg.id}>
                            <td>{reg.name}</td>
                            <td>{reg.phone}</td>
                            <td>{reg.email || '-'}</td>
                            <td>
                              <label style={{ display: 'flex', alignItems: 'center', cursor: canManageRegistrations ? 'pointer' : 'default', gap: '0.5rem' }}>
                                <input
                                  type="checkbox"
                                  checked={reg.accept_messages}
                                  onChange={() => canManageRegistrations && handleToggleAcceptMessages(reg.id)}
                                  disabled={!canManageRegistrations}
                                  style={{ cursor: canManageRegistrations ? 'pointer' : 'not-allowed', width: '18px', height: '18px' }}
                                />
                                <span style={{ fontSize: '0.875rem' }}>
                                  {reg.accept_messages ? '✅' : '❌'}
                                </span>
                              </label>
                            </td>
                            <td>{format(new Date(reg.created_at), 'dd/MM/yyyy')}</td>
                            <td>
                              {canManageRegistrations && (
                                <button
                                  onClick={() => handleDeleteRegistration(reg.id)}
                                  className="btn btn-sm btn-danger"
                                >
                                  Excluir
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
        )}

        {activeTab === 'reminders' && (
          <div className="tab-content">
            {canManageReminders && (
              <Link to={`/events/${id}/reminders/new`} className="btn btn-primary">
                + Novo Lembrete
              </Link>
            )}
            {reminders.length === 0 ? (
              <p style={{ marginTop: '1rem' }}>Nenhum lembrete cadastrado</p>
            ) : (
              <div style={{ marginTop: '1rem' }}>
                {reminders.map((reminder) => (
                  <div key={reminder.id} className="card" style={{ marginBottom: '1rem' }}>
                    <h3>{reminder.days_before} dias antes</h3>
                    <div
                      className="reminder-message"
                      dangerouslySetInnerHTML={{ __html: renderPreview(reminder.message) }}
                    />
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-light)' }}>
                      {reminder.sent ? '✓ Enviado' : '⏳ Pendente'}
                    </p>
                    <div className="reminder-actions-row">
                      {!reminder.sent && canManageReminders && (
                        <>
                          <Link
                            to={`/events/${id}/reminders/${reminder.id}/edit`}
                            className="btn btn-sm btn-outline"
                          >
                            Editar
                          </Link>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteReminder(reminder.id)}
                          >
                            Excluir
                          </button>
                        </>
                      )}
                      {canManageReminders && (
                        <button
                          className="btn btn-sm btn-outline"
                          onClick={() => handleDuplicateReminder(reminder)}
                          title="Criar novo lembrete com base neste"
                        >
                          📋 Duplicar
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'collaborators' && (
          <div className="tab-content">
            <button 
              onClick={() => setAddCollaboratorModalOpen(true)} 
              className="btn btn-primary"
            >
              + Adicionar Colaborador
            </button>
            
            {collaborators.length === 0 ? (
              <p style={{ marginTop: '1rem' }}>Nenhum colaborador cadastrado</p>
            ) : (
              <table className="table" style={{ marginTop: '1rem' }}>
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>Email</th>
                    <th>Permissão</th>
                    <th>Cadastrado em</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {collaborators.map((collab) => (
                    <tr key={collab.id}>
                      <td>{collab.name}</td>
                      <td>{collab.email}</td>
                      <td>
                        <select
                          value={collab.role}
                          onChange={(e) => handleUpdateRole(collab.id, e.target.value)}
                          className="form-select"
                        >
                          <option value="viewer">Visualizador</option>
                          <option value="editor">Editor</option>
                          <option value="manager">Gerente</option>
                        </select>
                      </td>
                      <td>{format(new Date(collab.created_at), 'dd/MM/yyyy')}</td>
                      <td>
                        <button
                          onClick={() => handleResetPassword(collab.id, collab.name)}
                          className="btn btn-sm btn-warning"
                          style={{ marginRight: '0.5rem' }}
                          title="Resetar senha"
                        >
                          Resetar Senha
                        </button>
                        <button
                          onClick={() => handleRemoveCollaborator(collab.id)}
                          className="btn btn-sm btn-danger"
                        >
                          Remover
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {addCollaboratorModalOpen && (
        <div className="modal-overlay" onClick={closeCollaboratorModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{tempPassword && !newCollaborator.name ? 'Senha Resetada' : 'Adicionar Colaborador'}</h2>
            {!tempPassword ? (
              <form onSubmit={handleAddCollaborator}>
                <div className="form-group">
                  <label>Nome *</label>
                  <input
                    type="text"
                    value={newCollaborator.name}
                    onChange={(e) => setNewCollaborator({...newCollaborator, name: e.target.value})}
                    required
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={newCollaborator.email}
                    onChange={(e) => setNewCollaborator({...newCollaborator, email: e.target.value})}
                    required
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Permissão</label>
                  <select
                    value={newCollaborator.role}
                    onChange={(e) => setNewCollaborator({...newCollaborator, role: e.target.value})}
                    className="form-control"
                  >
                    <option value="viewer">Visualizador - Apenas visualiza</option>
                    <option value="editor">Editor - Gerencia inscrições e lembretes</option>
                    <option value="manager">Gerente - Pode editar o evento</option>
                  </select>
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-outline" onClick={closeCollaboratorModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Adicionar
                  </button>
                </div>
              </form>
            ) : (
              <div>
                <p>{tempPassword && !newCollaborator.name ? 'Senha resetada com sucesso!' : '✅ Colaborador adicionado com sucesso!'}</p>
                <p style={{ marginTop: '1rem' }}>
                  <strong>Senha temporária:</strong>
                </p>
                <div style={{ 
                  backgroundColor: '#f5f5f5', 
                  padding: '1rem', 
                  borderRadius: '8px',
                  fontFamily: 'monospace',
                  fontSize: '1.25rem',
                  textAlign: 'center',
                  marginTop: '0.5rem'
                }}>
                  {tempPassword}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-light)', marginTop: '1rem' }}>
                  ⚠️ Envie esta senha para o colaborador. <span style={{ fontStyle: 'italic', color: 'var(--warning-color)' }}>(por WhatsApp, SMS ou outro meio seguro)</span> Ele será solicitado a alterá-la no primeiro login.
                </p>
                <div className="modal-actions">
                  <button 
                    className="btn btn-outline" 
                    onClick={() => {
                      navigator.clipboard.writeText(tempPassword);
                      toast.success('Senha copiada!');
                    }}
                  >
                    📋 Copiar Senha
                  </button>
                  <button 
                    className="btn btn-outline" 
                    onClick={() => {
                      navigator.clipboard.writeText("https://eventoz.com.br/login");
                      toast.success('Link copiado!');
                    }}
                  >
                    📋 Copiar Link
                  </button>
                  <button className="btn btn-primary" onClick={closeCollaboratorModal}>
                    Fechar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {winnerModalOpen && winnerData && (
        <div className="modal-overlay" onClick={closeWinnerModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>🎉 Vencedor do sorteio</h2>
            <p><strong>Nome:</strong> {winnerData.name}</p>
            <p><strong>Telefone:</strong> {winnerData.phone}</p>
            <p><strong>Email:</strong> {winnerData.email || '—'}</p>
            <div className="modal-actions">
              <button className="btn" onClick={closeWinnerModal}>Fechar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetails;
